// Catalog.ID Client — Argon2id Key Derivation
// ============================================================================
// Derives an AES-256-GCM key from a supersecret string.
// Parameters match the server: 64 MB memory, 3 iterations, 4 parallelism.
// Uses hash-wasm WASM implementation.

import { argon2id } from 'hash-wasm';
import { extractRandomBytes } from './supersecret';

/** Argon2id parameters matching the whitepaper §3.4.3 and server implementation. */
export const ARGON2_PARAMS = {
    memorySize: 65536,  // 64 MB in KiB
    iterations: 3,
    parallelism: 4,
    hashLength: 32,     // AES-256 key = 32 bytes
} as const;

/**
 * Derive a 32-byte AES-256-GCM key from a supersecret and salt using Argon2id.
 * The Argon2id input is the 44 random ASCII bytes of the supersecret (no dashes, no checksums).
 * @param supersecret The extended supersecret (4 lines of 12 characters)
 * @param salt Random salt bytes (stored on the server alongside the encrypted key bag)
 * @returns 32-byte derived key
 */
export async function deriveKeyFromSupersecret(supersecret: string, salt: Uint8Array): Promise<Uint8Array> {
    const passwordBytes = extractRandomBytes(supersecret);
    const hash = await argon2id({
        password: passwordBytes,
        salt,
        ...ARGON2_PARAMS,
        outputType: 'binary',
    });
    return new Uint8Array(hash);
}

/** Generate a random salt for Argon2id. */
export function generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(16));
}
